// Step 2 (Deliveries hub) of CallOffWizard.tsx, split out to keep
// CallOffWizard.tsx under the 500-line cap. No behaviour change from the
// original inline JSX — moved verbatim.
import { Plus, Warehouse, MapPin, Trash2, Pencil, Flag } from 'lucide-react';
import type { HaulierOption, WizardDelivery } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Progress } from '../../ui/progress';
import { Card, CardContent } from '../../ui/card';
import CallOffHaulierSelect from './CallOffHaulierSelect';

interface CallOffWizardDeliveriesStepProps {
  generatedReference: string;
  supplierName: string | undefined;
  pickupDate: string;
  setPickupDate: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  /** CR-7732: the customer's hauliers and the chosen one. */
  haulierOptions: HaulierOption[];
  loadingHauliers: boolean;
  haulierId: number | null;
  setHaulierId: (value: number | null) => void;
  /** CR-7732: true once the load is in transit — the picker is read-only. */
  haulierLocked: boolean;
  fillPercent: number;
  totalQty: number;
  deliveries: WizardDelivery[];
  hasStaleDeliveries: boolean;
  isStaleDelivery: (delivery: WizardDelivery) => boolean;
  handleEditDelivery: (index: number) => void;
  handleRemoveDelivery: (index: number) => void;
  onAddDelivery: () => void;
}

export default function CallOffWizardDeliveriesStep({
  generatedReference,
  supplierName,
  pickupDate,
  setPickupDate,
  notes,
  setNotes,
  haulierOptions,
  loadingHauliers,
  haulierId,
  setHaulierId,
  haulierLocked,
  fillPercent,
  totalQty,
  deliveries,
  hasStaleDeliveries,
  isStaleDelivery,
  handleEditDelivery,
  handleRemoveDelivery,
  onAddDelivery,
}: CallOffWizardDeliveriesStepProps) {
  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs">Reference</Label>
          <p className="font-mono text-sm">{generatedReference}</p>
        </div>
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs">Supplier</Label>
          <p className="text-sm">{supplierName}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Pickup Date <span className="text-red-500">*</span></Label>
          <Input
            type="date"
            value={pickupDate}
            onChange={(e) => setPickupDate(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes"
            rows={2}
          />
        </div>
      </div>

      {/* CR-7732: which haulier collects this load */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Haulier</Label>
          <CallOffHaulierSelect
            options={haulierOptions}
            value={haulierId}
            onChange={setHaulierId}
            disabled={haulierLocked}
            loading={loadingHauliers}
          />
          {haulierLocked ? (
            <p className="text-xs text-muted-foreground">
              The haulier can't be changed once the load is in transit.
            </p>
          ) : !loadingHauliers && haulierOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No hauliers are linked to your company yet.
            </p>
          ) : null}
        </div>
      </div>

      {/* Container fill bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Container Fill</span>
          <span className={`font-bold ${fillPercent > 90 ? 'text-red-600' : fillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
            {fillPercent}%
          </span>
        </div>
        <Progress value={Math.min(fillPercent, 100)} className="h-3" />
        <p className="text-xs text-muted-foreground">
          {totalQty} total units across {deliveries.length} {deliveries.length === 1 ? 'delivery' : 'deliveries'}
        </p>
      </div>

      {/* Delivery cards */}
      {deliveries.length > 0 && (
        <div className="space-y-2">
          <Label>Deliveries</Label>
          {hasStaleDeliveries && (
            <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
              <span className="font-semibold">⚠ Action required:</span>
              <span>
                One or more deliveries point to a warehouse that no longer exists.
                Edit each flagged delivery and pick a current warehouse before saving.
              </span>
            </div>
          )}
          {deliveries.map((delivery, idx) => {
            const stale = isStaleDelivery(delivery);
            // CR-7744: the delivery's priority parts, named in the tooltip.
            const priorityParts = delivery.parts.filter(p => p.isPriority).map(p => p.partNumber);
            return (
              <Card
                key={idx}
                className={`border-l-4 ${
                  stale
                    ? 'border-l-red-600 bg-red-50/50 ring-1 ring-red-300'
                    : delivery.type === 'warehouse'
                      ? 'border-l-red-500'
                      : 'border-l-blue-500'
                }`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {delivery.type === 'warehouse' ? (
                        <Warehouse className={`h-4 w-4 ${stale ? 'text-red-600' : 'text-red-500'}`} />
                      ) : (
                        <MapPin className="h-4 w-4 text-blue-500" />
                      )}
                      <div>
                        <p className={`text-sm font-medium ${stale ? 'text-red-700' : ''}`}>
                          {stale
                            ? `⚠ Unknown destination${delivery.destinationName ? ` (was: ${delivery.destinationName})` : ''} — please re-select`
                            : delivery.destinationName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {delivery.type === 'warehouse' ? 'Warehouse' : 'Direct'} &middot;{' '}
                          {delivery.parts.length} {delivery.parts.length === 1 ? 'part' : 'parts'} &middot;{' '}
                          {delivery.parts.reduce((sum, p) => sum + p.qty, 0)} units
                          {priorityParts.length > 0 && (
                            <>
                              {' '}&middot;{' '}
                              <span
                                className="inline-flex items-center gap-0.5 font-semibold text-orange-700"
                                title={`Priority: ${priorityParts.join(', ')}`}
                              >
                                <Flag className="h-3 w-3" aria-hidden="true" />
                                {priorityParts.length} priority
                              </span>
                            </>
                          )}
                        </p>
                        {delivery.type === 'direct' && delivery.address && (
                          <p className="text-xs text-muted-foreground">{delivery.address}, {delivery.postcode}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditDelivery(idx)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveDelivery(idx)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add delivery button */}
      <Button variant="outline" className="w-full gap-2" onClick={onAddDelivery}>
        <Plus className="h-4 w-4" />
        Add Delivery
      </Button>
    </div>
  );
}
